import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Empty } from 'antd';
import { ChevronDownIcon } from '@/assets/icons/icons';

type City = {
  id: string | number;
  name?: string;
  display?: string;
};

type StateRow = {
  id: string | number;
  name?: string;
  display_name?: string;
  cities?: City[];
};

type SelectedOption = {
  id: string | number;
  name?: string;
};

type Props = {
  source: StateRow[];
  searchQuery?: string;
  selectedOptions: SelectedOption[];
  setSelectedOptions?: React.Dispatch<React.SetStateAction<SelectedOption[]>>;
  hiddenOptionIds?: Set<string>;
  /** When Select all is used with search, load every matching page first. */
  fetchAllMatching?: () => Promise<StateRow[]>;
};

const getStateLabel = (state: StateRow) =>
  state.display_name || state.name || String(state.id);

const getCityLabel = (city: City) =>
  city.display || city.name || String(city.id);

const LocationsGroupedSubFilterMenuBody: React.FC<Props> = ({
  source = [],
  searchQuery = '',
  selectedOptions = [],
  setSelectedOptions,
  hiddenOptionIds = new Set(),
  fetchAllMatching,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Array<string | number>>(
    []
  );
  const [localSelected, setLocalSelected] = useState<SelectedOption[]>(
    selectedOptions || []
  );

  useEffect(() => {
    setLocalSelected(selectedOptions || []);
  }, [selectedOptions]);

  const selectedIds = useMemo(
    () => new Set(localSelected.map((opt) => String(opt.id))),
    [localSelected]
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current || !source.length) return;

    const toExpand = new Set<string | number>();

    source.forEach((state) => {
      const cities = Array.isArray(state.cities) ? state.cities : [];
      const hasSelectedChild = cities.some((city) =>
        selectedIds.has(String(city.id))
      );

      if (hasSelectedChild) {
        toExpand.add(state.id);
      }
    });

    setExpandedGroups(Array.from(toExpand));
    isInitialized.current = true;
  }, [source, selectedIds]);

  const toggleExpand = (id: StateRow['id']) => {
    setExpandedGroups((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const visibleSource = useMemo(() => {
    return source
      .map((state) => ({
        ...state,
        cities: (Array.isArray(state.cities) ? state.cities : []).filter(
          (city) => !hiddenOptionIds.has(String(city.id))
        ),
      }))
      .filter((state) => {
        const cities = Array.isArray(state.cities) ? state.cities : [];
        const stateHidden = hiddenOptionIds.has(String(state.id));
        return cities.length > 0 || !stateHidden;
      });
  }, [source, hiddenOptionIds]);
  const filteredSource = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleSource;

    return visibleSource
      .map((state) => {
        const stateLabel = getStateLabel(state).toLowerCase();
        const cities = Array.isArray(state.cities) ? state.cities : [];

        const matchedCities = cities.filter((city) =>
          getCityLabel(city).toLowerCase().includes(q)
        );

        if (stateLabel.includes(q)) {
          return state;
        }

        if (matchedCities.length) {
          return {
            ...state,
            cities: matchedCities,
          };
        }

        return null;
      })
      .filter(Boolean) as StateRow[];
  }, [searchQuery, visibleSource]);

  const addMany = (options: SelectedOption[]) => {
    const map = new Map<string, SelectedOption>();

    localSelected.forEach((opt) => map.set(String(opt.id), opt));
    options.forEach((opt) => map.set(String(opt.id), opt));

    const updated = Array.from(map.values());
    setLocalSelected(updated);
    setSelectedOptions?.(updated);
  };

  const removeManyByIds = (ids: Array<string | number>) => {
    const idSet = new Set(ids.map((id) => String(id)));
    const updated = localSelected.filter((opt) => !idSet.has(String(opt.id)));
    setLocalSelected(updated);
    setSelectedOptions?.(updated);
  };

  const setStateChecked = (state: StateRow, checked: boolean) => {
    const cities = Array.isArray(state.cities) ? state.cities : [];

    if (cities.length) {
      const cityOptions = cities.map((city) => ({
        id: city.id,
        name: getCityLabel(city),
      }));

      if (checked) {
        addMany(cityOptions);
        setExpandedGroups((prev) => Array.from(new Set([...prev, state.id])));
      } else {
        removeManyByIds(cities.map((city) => city.id));
      }
    } else {
      const option = {
        id: state.id,
        name: getStateLabel(state),
      };

      if (checked) addMany([option]);
      else removeManyByIds([state.id]);
    }
  };

  const setCityChecked = (city: City, checked: boolean) => {
    const option = {
      id: city.id,
      name: getCityLabel(city),
    };

    if (checked) addMany([option]);
    else removeManyByIds([city.id]);
  };

  const visibleIdList = useMemo(() => {
    const ids: Array<string | number> = [];

    filteredSource.forEach((state) => {
      const cities = Array.isArray(state.cities) ? state.cities : [];
      if (cities.length) {
        ids.push(...cities.map((city) => city.id));
      } else {
        ids.push(state.id);
      }
    });

    return ids;
  }, [filteredSource]);

  const visibleSelectedCount = useMemo(
    () =>
      visibleIdList.reduce<number>(
        (acc, id) => acc + (selectedIds.has(String(id)) ? 1 : 0),
        0
      ),
    [visibleIdList, selectedIds]
  );

  const allVisibleChecked =
    visibleIdList.length > 0 && visibleSelectedCount === visibleIdList.length;

  const filterStates = (rows: StateRow[], query: string): StateRow[] => {
    const visible = rows
      .map((state) => ({
        ...state,
        cities: (Array.isArray(state.cities)
          ? state.cities
          : Array.isArray(
                (state as StateRow & { locations?: City[] }).locations
              )
            ? (state as StateRow & { locations?: City[] }).locations || []
            : []
        ).filter((city) => !hiddenOptionIds.has(String(city.id))),
      }))
      .filter((state) => {
        const cities = Array.isArray(state.cities) ? state.cities : [];
        const stateHidden = hiddenOptionIds.has(String(state.id));
        return cities.length > 0 || !stateHidden;
      });

    const q = query.trim().toLowerCase();
    if (!q) return visible;

    return visible
      .map((state) => {
        const stateLabel = getStateLabel(state).toLowerCase();
        const cities = Array.isArray(state.cities) ? state.cities : [];
        const matchedCities = cities.filter((city) =>
          getCityLabel(city).toLowerCase().includes(q)
        );

        if (stateLabel.includes(q)) return state;
        if (matchedCities.length) return { ...state, cities: matchedCities };
        return null;
      })
      .filter(Boolean) as StateRow[];
  };

  const onToggleSelectAllVisible = async (checked: boolean) => {
    if (checked) {
      let rowsForSelect = filteredSource;

      if (searchQuery.trim() && fetchAllMatching) {
        const all = await fetchAllMatching();
        rowsForSelect = filterStates(all || [], searchQuery);
      }

      const options: SelectedOption[] = [];

      rowsForSelect.forEach((state) => {
        const cities = Array.isArray(state.cities) ? state.cities : [];

        if (cities.length) {
          cities.forEach((city) =>
            options.push({
              id: city.id,
              name: getCityLabel(city),
            })
          );
        } else {
          options.push({
            id: state.id,
            name: getStateLabel(state),
          });
        }
      });

      addMany(options);

      const toExpand = rowsForSelect
        .filter((state) => (state.cities?.length || 0) > 0)
        .map((state) => state.id);

      setExpandedGroups((prev) => Array.from(new Set([...prev, ...toExpand])));
    } else {
      // When a search is active, remove the full match set (not just the
      // currently rendered page), matching Select-all check behavior.
      let idsToRemove = visibleIdList;
      if (searchQuery.trim() && fetchAllMatching) {
        const all = await fetchAllMatching();
        const rowsForClear = filterStates(all || [], searchQuery);
        idsToRemove = [];
        rowsForClear.forEach((state) => {
          const cities = Array.isArray(state.cities) ? state.cities : [];
          if (cities.length) {
            idsToRemove.push(...cities.map((city) => city.id));
          } else {
            idsToRemove.push(state.id);
          }
        });
      }
      removeManyByIds(idsToRemove);
    }
  };

  if (!filteredSource.length) {
    return (
      <div style={{ padding: '16px 12px' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='No locations'
        />
      </div>
    );
  }

  return (
    <div
      style={{
        maxHeight: 320,
        overflowY: 'auto',
        padding: '0 5px 8px',
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <Checkbox
          className='custom-menu-shipment-checkbox-only checkbox-sm'
          checked={allVisibleChecked}
          onChange={(e) => onToggleSelectAllVisible(e.target.checked)}
          style={{ fontSize: 12 }}
        >
          Select all
        </Checkbox>
      </div>

      {filteredSource.map((state) => {
        const stateLabel = getStateLabel(state);
        const cities = Array.isArray(state.cities) ? state.cities : [];
        const cityIds = cities.map((city) => String(city.id));

        const groupSelected = cities.length
          ? cityIds.every((id) => selectedIds.has(id))
          : selectedIds.has(String(state.id));

        const isExpanded = expandedGroups.includes(state.id);

        return (
          <div key={String(state.id)} style={{ marginBottom: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Checkbox
                className='custom-menu-shipment-checkbox-only checkbox-sm'
                checked={groupSelected}
                onChange={(e) => setStateChecked(state, e.target.checked)}
                style={{ fontSize: 12 }}
              >
                {stateLabel} ({cityIds.length})
              </Checkbox>

              {!!cityIds.length && (
                <button
                  onClick={() => toggleExpand(state.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  type='button'
                >
                  <ChevronDownIcon
                    className={`chevron-icon ${isExpanded ? 'rotated' : ''}`}
                  />
                </button>
              )}
            </div>

            {isExpanded && !!cityIds.length && (
              <div style={{ marginLeft: 20, marginTop: 6 }}>
                {cities.map((city) => {
                  const cityLabel = getCityLabel(city);
                  const cityChecked = selectedIds.has(String(city.id));

                  return (
                    <Checkbox
                      key={String(city.id)}
                      className='custom-menu-shipment-checkbox-only checkbox-sm'
                      style={{ display: 'flex', marginBottom: 8, fontSize: 12 }}
                      checked={cityChecked}
                      onChange={(e) => setCityChecked(city, e.target.checked)}
                    >
                      {cityLabel}
                    </Checkbox>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LocationsGroupedSubFilterMenuBody;
